import React, { useEffect, useMemo, useState } from "react";

import { Button } from "@/design-system/components/Button/Button";
import { Checkbox } from "@/design-system/components/Checkbox/Checkbox";
import { Dialog } from "@/design-system/components/Dialog/Dialog";
import { Select } from "@/design-system/components/Select/Select";
import usePermissions from "@/access-control/queries/usePermissions";
import { groupPermissions } from "@/serviceAccounts/permissions";
import { PermissionsGrid } from "@/serviceAccounts/PermissionsGrid";
import { usePermissionCatalog } from "@/serviceAccounts/queries";
import { roleToReadableName } from "@/TeamPage/roleOptions";
import {
  clearSimulatedAccess,
  setSimulatedAccess,
  simulatedAccess,
} from "@/util";

interface SimulatePermissionsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Non-empty sentinels: Radix Select forbids an empty-string item value.
const CUSTOM_ROLE = "custom";
const NONE_ROLE = "none";

// Selected permissions use the catalog's canonical "<domain>:<level>" strings,
// matching the API-token grid.
function permissionsToSet(permissions: Record<string, string[]>): Set<string> {
  const set = new Set<string>();
  for (const [domain, levels] of Object.entries(permissions)) {
    for (const level of levels) set.add(`${domain}:${level}`);
  }
  return set;
}

function setToPermissions(set: Set<string>): Record<string, string[]> {
  const permissions: Record<string, string[]> = {};
  for (const permission of set) {
    const [domain, level] = permission.split(":");
    if (!domain || !level) continue;
    (permissions[domain] ??= []).push(level);
  }
  return permissions;
}

export function SimulatePermissionsModal({
  open,
  onOpenChange,
}: SimulatePermissionsModalProps) {
  const { data: catalog, isLoading: catalogLoading } = usePermissionCatalog();
  const { data: rolePermissions } = usePermissions();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectedRole, setSelectedRole] = useState<string>(CUSTOM_ROLE);
  const [backendOnly, setBackendOnly] = useState(false);

  const permissionGroups = useMemo(
    () => (catalog ? groupPermissions(catalog) : []),
    [catalog],
  );

  const roleOptions = useMemo(() => {
    const roles = rolePermissions ? Object.keys(rolePermissions).sort() : [];
    return [
      { value: CUSTOM_ROLE, label: "Custom" },
      { value: NONE_ROLE, label: "None" },
      ...roles.map((role) => ({
        value: role,
        label: roleToReadableName(role),
      })),
    ];
  }, [rolePermissions]);

  useEffect(() => {
    if (!open) return;
    const active = simulatedAccess();
    setSelected(active ? permissionsToSet(active.permissions) : new Set());
    setSelectedRole(CUSTOM_ROLE);
    setBackendOnly(active?.backendOnly ?? false);
  }, [open]);

  const applyRole = (role: string) => {
    setSelectedRole(role);
    if (role === CUSTOM_ROLE) {
      return;
    }
    if (role === NONE_ROLE) {
      setSelected(new Set());
      return;
    }
    setSelected(permissionsToSet(rolePermissions?.[role] ?? {}));
  };

  const togglePermission = (permission: string) => {
    setSelectedRole(CUSTOM_ROLE);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(permission)) {
        next.delete(permission);
      } else {
        next.add(permission);
      }
      return next;
    });
  };

  const handleSave = () => {
    setSimulatedAccess({
      permissions: setToPermissions(selected),
      backendOnly,
    });
    window.location.reload();
  };

  const handleStop = () => {
    clearSimulatedAccess();
    window.location.reload();
  };

  const isSimulating = simulatedAccess() !== null;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Simulate permissions"
      description="Restrict your own access to test what a customer with a limited permission set sees. Persists for 2 hours and reloads the page."
      contentProps={{ className: "w-[40rem]" }}
    >
      <div className="flex flex-col gap-6">
        <section className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-4">
            <h3 className="text-textDefault text-sm font-semibold">
              Permissions
            </h3>
            <div className="w-48">
              <Select
                options={roleOptions}
                getOptionLabel={(opt) => opt.label}
                getOptionValue={(opt) => opt.value}
                rootProps={{
                  value: selectedRole,
                  onValueChange: applyRole,
                }}
                triggerProps={{ className: "w-full" }}
              />
            </div>
          </div>

          <PermissionsGrid
            permissionGroups={permissionGroups}
            loading={catalogLoading}
            value={selected}
            onToggle={togglePermission}
          />
        </section>

        <label className="flex cursor-pointer items-start gap-3">
          <Checkbox
            className="mt-0.5 shrink-0"
            checked={backendOnly}
            onCheckedChange={(checked) => setBackendOnly(checked === true)}
          />
          <span className="flex flex-col gap-1">
            <span className="text-textDefault text-sm font-semibold">
              Simulate on backend only
            </span>
            <span className="text-textSubtle text-xs">
              Frontend can&apos;t actually enforce RBAC, it just makes a nicer
              UX for restricted permissions. To test that RBAC is a hard
              security boundary, simulate it on the backend only, while keeping
              your superuser permissions on the frontend, and confirm that
              you&apos;re still not able to access anything unauthorized, even
              though the frontend lets you try.
            </span>
          </span>
        </label>

        <div className="flex items-center justify-between gap-3">
          {isSimulating ? (
            <Button variant="ghost" onClick={handleStop}>
              Stop simulating
            </Button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Simulate</Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
